import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { Building2, CalendarClock, Radio, UserRound, TrendingUp, TriangleAlert } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { StatusPill } from '@/components/StatusPill';
import { SearchInput } from '@/components/SearchInput';
import { Pagination } from '@/components/Pagination';
import { Sparkline } from '@/components/Sparkline';
import { KpiCard } from '@/components/insights/KpiCard';
import { TableSkeleton } from '@/components/insights/Skeleton';
import { relative } from '@/components/insights/HealthGauge';
import { companiesService, CompaniesSummary, Company, IndustryLinkReport, ListCompaniesResponse } from '@/services/companies.service';
import { industryIcon } from '@/pages/industry-configs/industry-meta';
import { LINK_META, industryGroup, industryLabel } from '@/components/industry/industry-link-meta';
import { IndustryLinkReview } from '@/components/industry/IndustryLinkReview';

// Tabs map 1:1 onto the server's status filter — the old Live/Not-live split
// was applied client-side to a single page and silently hid matches.
type StatusTab = 'all' | 'active' | 'trial' | 'suspended' | 'expired';
const TABS: Array<{ key: StatusTab; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'trial', label: 'Trial' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'expired', label: 'Expired' },
];
const LIMIT = 20;
const DAY = 864e5;

function expiry(iso: string): { text: string; cls: string } {
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / DAY);
  if (days < 0) return { text: `Expired ${-days}d ago`, cls: 'bg-danger/15 text-danger' };
  if (days === 0) return { text: 'Expires today', cls: 'bg-danger/15 text-danger' };
  if (days <= 14) return { text: `${days}d left`, cls: 'bg-warning/15 text-warning' };
  return {
    text: new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }),
    cls: 'text-text-secondary',
  };
}

/** "Olive & Ember Restaurant" → "OE" — symbols are not initials. */
function initialsOf(name: string) {
  return name.split(/\s+/).filter((w) => /^[\p{L}\p{N}]/u.test(w)).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

const bareDomain = (d: string) => d.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/+$/, '');

function CompanyLogo({ c }: { c: Company }) {
  // A broken logo used to hide itself and leave no avatar, knocking the name out of line.
  const [broken, setBroken] = useState(false);
  const initials = initialsOf(c.name);
  return c.logoUrl && !broken ? (
    <img
      src={c.logoUrl}
      alt=""
      className="h-9 w-9 shrink-0 rounded-full border border-border bg-surface object-cover"
      onError={() => setBroken(true)}
    />
  ) : (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
      {initials || '·'}
    </span>
  );
}

/** Industry as assigned here, flagged when the client's CRM runs as something else. */
function IndustryCell({ c }: { c: Company }) {
  const state = c.industryState;
  const shown = c.industry || (state === 'crm_only' ? c.profileIndustry : null);
  if (!shown) return <span className="text-xs text-text-muted">—</span>;
  const Icon = industryIcon(shown, industryGroup(shown));
  const flag = state === 'mismatch' || state === 'crm_only' || state === 'awaiting_onboarding' ? LINK_META[state] : null;
  const FlagIcon = flag?.icon;
  const title = flag
    ? `${flag.label}: ${flag.explain}${state === 'mismatch' ? ` (CRM runs as ${industryLabel(c.profileIndustry)})` : ''}`
    : undefined;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap" title={title}>
      <span className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
        state === 'crm_only' ? 'border border-dashed border-border-strong text-text-muted' : 'bg-surface-alt text-text-secondary',
      )}>
        <Icon size={11} /> {industryLabel(shown)}
      </span>
      {FlagIcon && flag.tone !== 'info' && <FlagIcon size={13} className={flag.tone === 'danger' ? 'text-danger' : 'text-warning'} />}
    </span>
  );
}

const TH = ({ children, className }: { children?: React.ReactNode; className?: string }) => (
  <th className={clsx('whitespace-nowrap px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wide text-text-muted', className)}>{children}</th>
);

export default function CompaniesList() {
  const [data, setData] = useState<ListCompaniesResponse | null>(null);
  const [summary, setSummary] = useState<CompaniesSummary | null>(null);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<StatusTab>('all');
  const [linkReport, setLinkReport] = useState<IndustryLinkReport | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => { companiesService.summary().then(setSummary).catch(() => setSummary(null)); }, []);
  useEffect(() => { companiesService.industryLink().then(setLinkReport).catch(() => setLinkReport(null)); }, [reload]);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    companiesService
      .list({ q: q || undefined, status: tab === 'all' ? undefined : tab, page, limit: LIMIT })
      .then((r) => { if (!cancelled) setData(r); })
      .catch(() => { if (!cancelled) setData({ items: [], total: 0, page: 1, limit: LIMIT }); });
    return () => { cancelled = true; };
  }, [q, page, tab, reload]);

  const rows = data?.items ?? [];
  const signups = summary?.signupsByDay.map((d) => d.count) ?? [];
  const signups30 = signups.reduce((a, b) => a + b, 0);

  return (
    <>
      <Topbar
        title="Companies"
        breadcrumb="Customers / Companies"
        right={<Link to="/companies/new"><Button>+ Create Client</Button></Link>}
      />
      <div className="space-y-5 p-6">
        {/* ── Summary strip ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <KpiCard icon={<Building2 size={14} />} label="Clients" value={summary?.total ?? 0} delay={0} />
          <KpiCard icon={<Radio size={14} />} label="Live" value={summary?.live ?? 0} tone="success" hint="Active + trial" delay={40} />
          <KpiCard
            icon={<CalendarClock size={14} />}
            label="Expiring ≤ 14d"
            value={summary?.expiringSoon ?? 0}
            tone={(summary?.expiringSoon ?? 0) > 0 ? 'warn' : 'default'}
            hint={(summary?.expiringSoon ?? 0) > 0 ? 'Needs a renewal nudge' : 'Nothing due'}
            delay={80}
          />
          <KpiCard icon={<UserRound size={14} />} label="Users" value={summary?.usersTotal ?? 0} hint="Across all clients" delay={120} />
          <div className="fade-up rounded-xl border border-border bg-surface p-4" style={{ animationDelay: '160ms' }}>
            <div className="flex items-center justify-between">
              <span className="text-2xs font-semibold uppercase tracking-[0.08em] text-text-muted">Signups · 30d</span>
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15 text-primary"><TrendingUp size={14} /></span>
            </div>
            <div className="mt-2 flex items-end justify-between gap-3">
              <span className="text-[26px] font-bold leading-none tabular-nums tracking-tight text-text-primary">{signups30}</span>
              <Sparkline data={signups} width={88} height={30} />
            </div>
          </div>
        </div>

        {/* ── Industry link review ─────────────────────────────────────── */}
        {!!linkReport?.needsReview && (
          <div className="fade-up flex flex-wrap items-center gap-3 rounded-xl border border-warning/30 bg-warning-soft px-4 py-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-warning/15 text-warning"><TriangleAlert size={16} /></span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-text-primary">
                {linkReport.needsReview} compan{linkReport.needsReview === 1 ? 'y needs' : 'ies need'} an industry review
              </div>
              <div className="text-xs text-text-muted">
                Their CRM runs as a different industry than the one assigned here, or none is assigned here yet.
              </div>
            </div>
            <Button size="sm" onClick={() => setReviewOpen(true)}>Review</Button>
          </div>
        )}

        {/* ── Toolbar ───────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-lg border border-border bg-surface p-0.5">
            {TABS.map(({ key, label }) => {
              const n = key === 'all' ? summary?.total : summary?.byStatus[key];
              return (
                <button
                  key={key}
                  onClick={() => { setTab(key); setPage(1); }}
                  className={clsx(
                    'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    tab === key ? 'bg-primary/15 text-primary' : 'text-text-muted hover:text-text-primary',
                  )}
                >
                  {label}
                  {summary && (
                    <span className={clsx('rounded-full px-1.5 text-2xs tabular-nums', tab === key ? 'bg-primary/20' : 'bg-surface-alt', !n && 'opacity-60')}>{n ?? 0}</span>
                  )}
                </button>
              );
            })}
          </div>
          <SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search by name…" />
          {data && <span className="ml-auto text-xs text-text-muted">{data.total.toLocaleString()} client{data.total === 1 ? '' : 's'}</span>}
        </div>

        {/* ── Table ─────────────────────────────────────────────────────── */}
        <div className="overflow-x-auto rounded-xl border border-border bg-surface">
          <table className="w-full text-sm">
            <thead className="bg-bg/40">
              <tr>
                <TH>Client</TH>
                <TH>Industry</TH>
                <TH>Admin</TH>
                <TH className="text-right">Users</TH>
                <TH>Messages · 30d</TH>
                <TH>Expiry</TH>
                <TH>Status</TH>
                <TH>Last active</TH>
              </tr>
            </thead>
            {!data ? <TableSkeleton cols={8} /> : (
              <tbody>
                {rows.map((c, i) => {
                  const ex = expiry(c.effectiveExpiresAt ?? c.expiresAt);
                  const status = c.effectiveStatus ?? c.status;
                  const spark = c.activitySpark ?? [];
                  const messages = spark.reduce((a, b) => a + b, 0);
                  const quiet = !c.lastActivityAt || Date.now() - new Date(c.lastActivityAt).getTime() > 14 * DAY;
                  return (
                    <tr
                      key={c.companyUid}
                      className="row-in border-t border-border transition-colors hover:bg-bg/40"
                      style={{ animationDelay: `${Math.min(i, 12) * 28}ms` }}
                    >
                      <td className="min-w-[220px] px-4 py-3">
                        <Link to={`/companies/${c.companyUid}`} className="group flex items-center gap-3">
                          <CompanyLogo c={c} />
                          <span className="min-w-0">
                            <span className="block truncate font-semibold text-text-primary group-hover:text-primary">{c.name}</span>
                            {/* The client code rides under the name — its own column pushed the table past the viewport. */}
                            <span className="block truncate text-[11px] text-text-muted">
                              {c.domain && <>{bareDomain(c.domain)} · </>}
                              <span className="font-mono uppercase tracking-wide">{c.slug}</span>
                            </span>
                          </span>
                        </Link>
                      </td>
                      <td className="px-4 py-3"><IndustryCell c={c} /></td>
                      <td className="max-w-[220px] truncate px-4 py-3 text-text-secondary" title={c.adminEmail}>{c.adminEmail ?? '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-text-secondary">{c.userCount ?? '—'}</td>
                      <td className="px-4 py-3">
                        {messages === 0 ? (
                          <span className="whitespace-nowrap text-xs text-text-muted">No messages</span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Sparkline data={spark} width={72} stroke={quiet ? 'var(--color-text-muted)' : 'var(--color-primary-dark)'} />
                            <span className="text-xs tabular-nums text-text-secondary">{messages.toLocaleString()}</span>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={clsx('inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium', ex.cls)}>{ex.text}</span>
                      </td>
                      <td className="px-4 py-3"><StatusPill status={status} /></td>
                      <td className={clsx('whitespace-nowrap px-4 py-3 text-xs', quiet ? 'text-text-muted' : 'text-text-secondary')}>{relative(c.lastActivityAt)}</td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-14 text-center text-text-muted">No clients match.</td></tr>
                )}
              </tbody>
            )}
          </table>
        </div>
        {data && <Pagination page={data.page} total={data.total} limit={data.limit} onChange={setPage} />}
      </div>
      <IndustryLinkReview
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        report={linkReport}
        onResolved={() => setReload((n) => n + 1)}
      />
    </>
  );
}
